import React, { useEffect, useCallback } from "react"; // Keep useCallback
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import Skeleton from "react-loading-skeleton";
import type { RootState, AppDispatch } from "../../../../app/store";
import ShippingAddressForm from "./ShippingAddressForm";
import {
  useGetCartQuery,
  useUpdateCustomerMutation,
} from "../../../cart/api/cartApi";
import { completeStep, setIsCurrentStepFormValid } from "../../checkoutSlice";
import RTKQErrorCard from "../../../../common/components/RTKQErrorCard";
import type { RTKQueryError } from "../../../../common/components/RTKQErrorCard";
import type { TransformedAddress } from "../../../cart/types/cartApiTypes";
import type { ShippingAddressFormProps } from "./ShippingAddressForm";
import type { TrCity, TrCityWithDistricts } from "../../types/checkoutApiTypes";

interface ShippingStepContainerProps {
  onComplete: () => void;
  cities?: TrCity[];
  citiesWithDistricts?: TrCityWithDistricts[];
  // REMOVED: onRegisterDirectSubmitHandler is no longer needed
}

// Helper: Map TransformedAddress to Shipping Form Data
const mapTransformedToShippingFormData = (
  address?: Partial<TransformedAddress>
): Partial<ShippingAddressFormProps["initialData"]> => {
  if (!address) return {};
  return {
    firstName: address.firstName || "",
    lastName: address.lastName || "",
    company: address.company || "",
    address1: address.address1 || "",
    address2: address.address2 || "",
    city: address.city || "", // District Name
    state: address.state || "", // State Code
    postcode: address.postcode || "",
    country: address.country || "", // Country Code
    phone: address.phone || "",
  };
};

const ShippingStepContainer: React.FC<ShippingStepContainerProps> = ({
  onComplete,
  cities,
  citiesWithDistricts,
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const formId = "shipping-address-form";
  const { data: cartData, isLoading: isCartLoading } = useGetCartQuery();
  const useBillingForShipping = useSelector(
    (state: RootState) => state.checkout.useBillingForShipping
  );
  const currentStep = useSelector(
    (state: RootState) => state.checkout.currentStep
  );
  const billingData = cartData?.billingAddress; // Need billing data
  const initialShippingData = mapTransformedToShippingFormData(
    cartData?.shippingAddress
  );
  const [
    updateCustomer,
    { isLoading: isUpdating, isError, error: updateError },
  ] = useUpdateCustomerMutation();

  // Effect to set step validity based on checkbox OR form validity (this logic is now handled within the form's effect)
  // We still need an effect here to handle the case where the user *checks* the box, making the step immediately valid.
  useEffect(() => {
    if (currentStep === "shipping") {
      // If using billing, validity is TRUE. If not, validity depends on the form (handled by form's effect).
      if (useBillingForShipping) {
        dispatch(setIsCurrentStepFormValid(true));
      }
      // When useBillingForShipping is false, the form's useEffect will dispatch the correct validity state.
    }
  }, [useBillingForShipping, currentStep, dispatch]);

  // --- MODIFIED API Submit Handler ---
  // This function is now called by ShippingAddressForm's onSubmit prop in BOTH cases.
  const handleApiSubmit = useCallback(
    async (formDataFromComponent: Partial<TransformedAddress>) => {
      let addressToSend: Partial<TransformedAddress>; // Use TransformedAddress directly

      if (useBillingForShipping) {
        // If checkbox is checked, IGNORE formDataFromComponent, use billingData
        console.log(
          "[ShippingStepContainer] Submitting Billing Address as Shipping"
        );
        if (!billingData) {
          console.error("Billing data not available for shipping submit.");
          // Handle error appropriately, maybe show message
          return;
        }
        addressToSend = billingData; // Use billing data directly (it's already TransformedAddress)
      } else {
        // If checkbox is unchecked, use the data submitted from the form
        console.log(
          "[ShippingStepContainer] Submitting Shipping Form Data:",
          formDataFromComponent
        );
        addressToSend = formDataFromComponent; // Use form data directly
      }

      if (!addressToSend || Object.keys(addressToSend).length === 0) {
        console.error("Could not create shipping payload.");
        // Handle error
        return;
      }

      try {
        // Pass the TransformedAddress directly to the mutation
        // The cartApi's updateCustomer will handle the transformation to snake_case
        await updateCustomer({ shippingAddress: addressToSend }).unwrap();
        dispatch(completeStep("shipping"));
        onComplete(); // Move to next step
      } catch (err) {
        console.error("Failed to update shipping address:", err);
        // Handle API error
      }
    },
    [useBillingForShipping, billingData, updateCustomer, dispatch, onComplete]
  );

  // Loading skeleton (remains the same)
  if (isCartLoading) {
    return (
      <div className="shipping-step-container skeleton-shipping">
        <h2>
          <Skeleton width={200} height={24} />
        </h2>
        <div className="form-field full-width use-billing-for-shipping">
          <Skeleton height={24} width={250} />
        </div>
        <div className="form-grid">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`form-field ${i > 1 ? "full-width" : ""}`}>
              <Skeleton height={16} width="40%" style={{ marginBottom: 5 }} />
              <Skeleton height={44} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="shipping-step-container">
      <h2>{t("checkout.shippingAddress", "Shipping Address")}</h2>
      {/* Error display logic remains the same */}
      {isError && !useBillingForShipping && (
        <RTKQErrorCard
          error={updateError as RTKQueryError | null}
          title={t("checkout.errors.updateFailed", "Failed to update address")}
          compact
        />
      )}
      <ShippingAddressForm
        formId={formId}
        onSubmit={handleApiSubmit} // Pass the unified handler
        initialData={initialShippingData}
        billingData={mapTransformedToShippingFormData(billingData)}
        isSubmitting={isUpdating}
        isDisabled={useBillingForShipping}
        cities={cities}
        citiesWithDistricts={citiesWithDistricts}
      />
    </div>
  );
};

export default ShippingStepContainer;
